import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ChevronDown, Loader2 } from 'lucide-react';
import './OrderManagement.css';
import { ORDER_STATUS } from "../../utils/orderStatus";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

//const validStatuses = ['Order Placed', 'Order Accepted', 'Work in Progress', 'Shipped', 'Cancelled'];

/*const validStatuses = [
    { value: "placed", label: "Order Placed" },
    { value: "accepted", label: "Order Accepted" },
    { value: "inprogress", label: "Work in Progress" },
    { value: "shipped", label: "Shipped" },
    { value: "cancelled", label: "Cancelled" }
];*/

const OrderManagement = ({ onBack, onOrderSelect, setToastMessage, setToast }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDropdownId, setOpenDropdownId] = useState(null);

    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdownId(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownRef]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/orders`);
            const data = await res.json();
            if (data.success) {
                // To display items, we need to fetch them for each order or fetch them all.
                // Actually our GET /api/orders doesn't return items. 
                // We will need to fetch items separately or update backend.
                // BUT the reference image shows thumbnails.
                // Let's just fetch full details for all orders for simplicity (since it's an admin dashboard and there might be a few orders, or we can update the backend to include basic item info).
                // Actually let's just make an API call to get items for each order, or better: update the backend to include items in getAllOrders. Wait, we can't change it easily now, so let's just use what we have, and showing thumbnails might require a second fetch.
                // For now, let's just fetch all orders then individually fetch their items.

                const allOrders = data.data;
                const ordersWithItems = await Promise.all(allOrders.map(async (order) => {
                    const itemRes = await fetch(`${API_URL}/orders/${order.id}`);
                    const itemData = await itemRes.json();
                    if (itemData.success) {
                        return itemData.data;
                    }
                    return order;
                }));

                setOrders(ordersWithItems);
            }
        } catch (err) {
            console.error("Error fetching orders:", err);
            setToastMessage("Failed to load orders");
            setToast(true);
            setTimeout(() => setToast(false), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (orderId, newStatus, e) => {
        e.stopPropagation(); // prevent opening order details
        setOpenDropdownId(null);

        try {
            const res = await fetch(`${API_URL}/orders/${orderId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            const data = await res.json();
            if (data.success) {
                setOrders(prevOrders =>
                    prevOrders.map(o => o.id === orderId ? { ...o, status: newStatus } : o)
                );
                setToastMessage(`Status updated to ${newStatus}`);
                setToast(true);
                setTimeout(() => setToast(false), 3000);
            } else {
                setToastMessage("Failed to update status");
                setToast(true);
                setTimeout(() => setToast(false), 3000);
            }
        } catch (err) {
            console.error("Error updating status:", err);
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };




    if (loading) {
        return (
            <div className="order-management-container" style={{ display: 'flex', justifyContent: 'center', paddingTop: '50px' }}>
                <Loader2 className="spin-icon" size={32} color="#db2777" />
            </div>
        );
    }

    return (
        <div className="order-management-container fade-in">
            <div className="order-management-header">
                <div className="back-link" onClick={onBack}>
                    <ArrowLeft size={16} />
                    <span>Back to Dashboard</span>
                </div>
                <h2 className="order-management-title">Order Management</h2>
                <p className="order-management-subtitle">View and manage customer orders</p>
            </div>

            {orders.length === 0 ? (
                <div className="empty-orders fade-in">
                    <p>No orders found.</p>
                </div>
            ) : (
                <div className="orders-list">
                    {orders.map(order => (
                        <div
                            key={order.id}
                            className={`order-card fade-in ${openDropdownId === order.id ? 'dropdown-open' : ''}`}
                            onClick={() => onOrderSelect(order)}
                        >
                            <div className="order-card-left">
                                <div className="order-header-info">
                                    <h3 className="order-id">Order #{order.id.split('-')[0]}</h3>
                                    <p className="order-date">{formatDateTime(order.created_at)}</p>
                                    <p className="order-customer">
                                        {order.delivery_name} &bull; {order.delivery_phone || order.delivery_email}
                                    </p>
                                </div>

                                <span className={`order-status-badge ${ORDER_STATUS[order.status]?.className}`}>
                                    {ORDER_STATUS[order.status]?.label}
                                </span>

                                {order.items && order.items.length > 0 && (
                                    <div className="order-thumbnails">
                                        {order.items.slice(0, 3).map((item, index) => (
                                            <img
                                                key={index}
                                                src={item.product_image || '/uploads/placeholder.jpg'}
                                                alt={item.product_name}
                                                className="order-thumb"
                                                onError={(e) => { e.target.src = '/uploads/placeholder.jpg'; }}
                                            />
                                        ))}
                                        {order.items.length > 3 && (
                                            <div className="more-thumbs-indicator">
                                                +{order.items.length - 3}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="order-card-right" onClick={(e) => e.stopPropagation()}>
                                <div className="order-total-wrapper">
                                    <p className="order-total-label">Total</p>
                                    <p className="order-total-amount">₹{Number(order.total_amount).toLocaleString('en-IN')}</p>
                                </div>

                                <div className="status-dropdown-container" ref={openDropdownId === order.id ? dropdownRef : null}>
                                    <button
                                        className="status-dropdown-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenDropdownId(openDropdownId === order.id ? null : order.id);
                                        }}
                                    >
                                        Update Status <ChevronDown size={14} />
                                    </button>

                                    {openDropdownId === order.id && (
                                        <div className="status-dropdown-menu">
                                            {Object.keys(ORDER_STATUS).map(key => (
                                                <div
                                                    key={key}
                                                    className={`status-dropdown-item ${order.status === key ? "active" : ""}`}
                                                    onClick={(e) => handleStatusUpdate(order.id, key, e)}
                                                >
                                                    {ORDER_STATUS[key].label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default OrderManagement;
